import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { FiRefreshCw, FiDownload } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import Logo from '../common/Logo';
import { getAggregateTimeSpentData, AggregateTimeUser, AggregateTimeSpentResponse } from '../../api/admin';
import { convertToCSV, downloadCSV, formatDateForFilename } from '../../utils/exportUtils';

// Access denied component
const AccessDenied = () => (
  <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex flex-col justify-center p-6">
    <div className="max-w-4xl mx-auto text-center">
      <h1 className="text-2xl font-bold text-red-600">Access Denied</h1>
      <p className="text-base text-gray-600 mt-2">You are not authorized to view this page.</p>
    </div>
  </div>
);

// Header component
const AggregateTimeHeader = () => (
  <h1 className="text-3xl font-bold text-center pt-4 mb-4 flex items-center justify-center gap-3">
    <Logo size={28} />
    <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-transparent bg-clip-text">
      Aggregate Time Spent Dashboard
    </span>
  </h1>
);

// Controls component
interface ControlsProps {
  onRefresh: () => void;
  onDownloadCSV: () => void;
  isLoading?: boolean;
  hasData?: boolean;
  dateRange: { startDate: string; endDate: string };
}

const Controls: React.FC<ControlsProps> = ({
  onRefresh,
  onDownloadCSV,
  isLoading = false,
  hasData = false,
  dateRange
}) => {
  return (
    <Card className="shadow-lg border border-gray-100 mb-4">
      <CardContent className="p-4">
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div className="text-sm text-gray-600">
            <p>📅 Data Range: {dateRange.startDate} - {dateRange.endDate}</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={onRefresh}
              disabled={isLoading}
              variant="outline"
              className="flex items-center px-4"
            >
              <FiRefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              onClick={onDownloadCSV}
              disabled={isLoading || !hasData}
              variant="outline"
              className="flex items-center px-4 bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
            >
              <FiDownload className="w-4 h-4 mr-2" />
              CSV
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Summary stats component
interface SummaryStatsProps {
  totalUsers: number;
  meetingTimeThreshold: number;
  meetingActiveTimeThreshold: number;
  meetingXPThreshold: number;
  isLoading?: boolean;
}

const SummaryStats: React.FC<SummaryStatsProps> = ({
  totalUsers,
  meetingTimeThreshold,
  meetingActiveTimeThreshold,
  meetingXPThreshold,
  isLoading = false
}) => {
  if (isLoading) {
    return (
      <Card className="shadow-lg border border-gray-100 mb-8">
        <CardContent className="p-4">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-300 rounded mb-4"></div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="text-center">
                  <div className="h-8 bg-gray-300 rounded mb-2"></div>
                  <div className="h-4 bg-gray-300 rounded"></div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-lg border border-gray-100 mb-8">
      <CardContent className="p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Overview</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-3 rounded-lg bg-gray-50">
            <p className="text-xl font-bold text-blue-600">
              {totalUsers.toLocaleString()}
            </p>
            <p className="text-xs font-medium text-gray-700 mb-1">
              Total Users
            </p>
            <p className="text-xs text-gray-500">
              All students in results
            </p>
          </div>

          <div className="text-center p-3 rounded-lg bg-gray-50">
            <p className="text-xl font-bold text-green-600">
              {meetingTimeThreshold}
            </p>
            <p className="text-xs font-medium text-gray-700 mb-1">
              Meeting Time Threshold
            </p>
            <p className="text-xs text-gray-500">
              {totalUsers > 0 ? ((meetingTimeThreshold / totalUsers) * 100).toFixed(1) : '0.0'}% • At or above allocated
            </p>
          </div>

          <div className="text-center p-3 rounded-lg bg-gray-50">
            <p className="text-xl font-bold text-green-600">
              {meetingActiveTimeThreshold}
            </p>
            <p className="text-xs font-medium text-gray-700 mb-1">
              Meeting Active Time Threshold
            </p>
            <p className="text-xs text-gray-500">
              {totalUsers > 0 ? ((meetingActiveTimeThreshold / totalUsers) * 100).toFixed(1) : '0.0'}% • At or above allocated
            </p>
          </div>

          <div className="text-center p-3 rounded-lg bg-gray-50">
            <p className="text-xl font-bold text-green-600">
              {meetingXPThreshold}
            </p>
            <p className="text-xs font-medium text-gray-700 mb-1">
              Meeting XP Threshold
            </p>
            <p className="text-xs text-gray-500">
              {totalUsers > 0 ? ((meetingXPThreshold / totalUsers) * 100).toFixed(1) : '0.0'}% • At or above allocated
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Table component
interface AggregateTimeTableProps {
  users: AggregateTimeUser[];
  isLoading?: boolean;
}

const AggregateTimeTable: React.FC<AggregateTimeTableProps> = ({
  users,
  isLoading = false
}) => {
  const [sortedUsers, setSortedUsers] = useState<AggregateTimeUser[]>([]);

  // Sort by XP descending by default
  useEffect(() => {
    const sorted = [...users].sort((a, b) => b.xpTotal - a.xpTotal);
    setSortedUsers(sorted);
  }, [users]);

  if (isLoading) {
    return (
      <Card className="shadow-lg border border-gray-100">
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-gray-300 rounded w-1/3 mb-6"></div>
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-300 rounded"></div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (sortedUsers.length === 0) {
    return (
      <Card className="shadow-lg border border-gray-100">
        <CardContent className="p-6 text-center">
          <div className="text-gray-400 mb-4">📊</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Data Found</h3>
          <p className="text-gray-600">
            No aggregate time data found.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Helper to calculate percentage and get badge class
  const getMetricDisplay = (actual: number, allocated: number, meetsMinimum: boolean) => {
    const percentage = allocated > 0 ? Math.round((actual / allocated) * 100) : 0;
    const badgeClass = meetsMinimum
      ? 'bg-green-100 text-green-800'
      : 'bg-red-100 text-red-800';
    return { percentage, badgeClass };
  };

  return (
    <Card className="shadow-lg border border-gray-100">
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User Email
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Age Grade
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Knowledge Grade
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Time Used
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Active Time
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  XP Earned
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedUsers.map((user, index) => {
                const timeDisplay = getMetricDisplay(
                  user.totalTimeHours,
                  user.allocatedTimeHours,
                  user.meetsTimeMinimum
                );
                const activeTimeDisplay = getMetricDisplay(
                  user.activeTimeHours,
                  user.allocatedTimeHours,
                  user.meetsActiveTimeMinimum
                );
                const xpDisplay = getMetricDisplay(
                  user.xpTotal,
                  user.allocatedXP,
                  user.meetsXPMinimum
                );

                return (
                  <tr key={`${user.userEmail}-${index}`} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {user.userEmail}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.ageGrade !== null ? `Grade ${user.ageGrade}` : 'N/A'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      Grade {user.knowledgeGrade}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${timeDisplay.badgeClass}`}>
                        {timeDisplay.percentage}% ({user.totalTimeHours.toFixed(1)} / {user.allocatedTimeHours.toFixed(1)} hrs)
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${activeTimeDisplay.badgeClass}`}>
                        {activeTimeDisplay.percentage}% ({user.activeTimeHours.toFixed(1)} / {user.allocatedTimeHours.toFixed(1)} hrs)
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${xpDisplay.badgeClass}`}>
                        {xpDisplay.percentage}% ({user.xpTotal.toFixed(1)} / {user.allocatedXP.toFixed(1)} XP)
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

// Error component
interface ErrorDisplayProps {
  error: string;
  onRetry: () => void;
}

const ErrorDisplay: React.FC<ErrorDisplayProps> = ({ error, onRetry }) => (
  <Card className="shadow-lg border border-red-100">
    <CardContent className="p-6 text-center">
      <div className="text-red-400 mb-4">⚠️</div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Data</h3>
      <p className="text-gray-600 mb-4">{error}</p>
      <Button onClick={onRetry} className="bg-red-600 hover:bg-red-700 text-white">
        <FiRefreshCw className="w-4 h-4 mr-2" />
        Try Again
      </Button>
    </CardContent>
  </Card>
);

// Main component
export const AggregateTimeSpentDashboard: React.FC = () => {
  const { user: currentUser } = useAuth();

  // Check if user is admin
  const isAdmin = useMemo(() =>
    currentUser?.role === 'admin',
    [currentUser?.role]
  );

  // State
  const [data, setData] = useState<AggregateTimeSpentResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    if (!data?.users) {
      return {
        totalUsers: 0,
        meetingTimeThreshold: 0,
        meetingActiveTimeThreshold: 0,
        meetingXPThreshold: 0
      };
    }

    return {
      totalUsers: data.users.length,
      meetingTimeThreshold: data.users.filter(u => u.isSpendingEnoughTime).length,
      meetingActiveTimeThreshold: data.users.filter(u => u.isSpendingEnoughActiveTime).length,
      meetingXPThreshold: data.users.filter(u => u.isSpendingEnoughXP).length
    };
  }, [data]);

  // Fetch data function
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await getAggregateTimeSpentData();
      setData(result);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch aggregate time spent data';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Retry function
  const handleRetry = useCallback(() => {
    fetchData();
  }, [fetchData]);

  // CSV download function
  const handleDownloadCSV = useCallback(() => {
    if (!data?.users || data.users.length === 0) return;

    const headers = [
      { key: 'userEmail' as keyof AggregateTimeUser, label: 'User Email' },
      { key: 'ageGrade' as keyof AggregateTimeUser, label: 'Age Grade' },
      { key: 'knowledgeGrade' as keyof AggregateTimeUser, label: 'Knowledge Grade' },
      { key: 'totalTimeHours' as keyof AggregateTimeUser, label: 'Total Time (hours)' },
      { key: 'activeTimeHours' as keyof AggregateTimeUser, label: 'Active Time (hours)' },
      { key: 'xpTotal' as keyof AggregateTimeUser, label: 'XP Earned' },
      { key: 'allocatedTimeHours' as keyof AggregateTimeUser, label: 'Allocated Time (hours)' },
      { key: 'allocatedXP' as keyof AggregateTimeUser, label: 'Allocated XP' },
      { key: 'isSpendingEnoughTime' as keyof AggregateTimeUser, label: 'Meets Allocated Time' },
      { key: 'isSpendingEnoughActiveTime' as keyof AggregateTimeUser, label: 'Meets Allocated Active Time' },
      { key: 'isSpendingEnoughXP' as keyof AggregateTimeUser, label: 'Meets Allocated XP' },
      { key: 'meetsTimeMinimum' as keyof AggregateTimeUser, label: 'Meets Daily Time Minimum' },
      { key: 'meetsActiveTimeMinimum' as keyof AggregateTimeUser, label: 'Meets Daily Active Time Minimum' },
      { key: 'meetsXPMinimum' as keyof AggregateTimeUser, label: 'Meets Daily XP Minimum' }
    ];

    const csvData = data.users.map(user => ({
      ...user,
      ageGrade: user.ageGrade !== null ? user.ageGrade : 'N/A'
    }));

    const csvContent = convertToCSV(csvData, headers);
    const filename = `aggregate-time-spent-${formatDateForFilename()}.csv`;

    downloadCSV(csvContent, filename);
  }, [data]);

  // Load data on component mount
  useEffect(() => {
    if (isAdmin) {
      fetchData();
    }
  }, [isAdmin, fetchData]);

  // Early return if not admin
  if (!isAdmin) {
    return <AccessDenied />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white p-4">
      <div className="max-w-7xl mx-auto space-y-4">
        <AggregateTimeHeader />

        {/* Navigation */}
        <div className="flex mb-4">
          <Link
            to="/admin"
            className="text-blue-600 hover:text-blue-800 transition-colors duration-300"
          >
            ← Back to Admin Dashboard
          </Link>
        </div>

        {/* Controls */}
        <Controls
          onRefresh={fetchData}
          onDownloadCSV={handleDownloadCSV}
          isLoading={isLoading}
          hasData={data?.users.length ? data.users.length > 0 : false}
          dateRange={data?.dateRange || { startDate: 'N/A', endDate: 'N/A' }}
        />

        {/* Error Display */}
        {error && (
          <ErrorDisplay error={error} onRetry={handleRetry} />
        )}

        {/* Dashboard Content */}
        {!error && (
          <>
            {/* Summary Statistics */}
            <SummaryStats
              totalUsers={summaryStats.totalUsers}
              meetingTimeThreshold={summaryStats.meetingTimeThreshold}
              meetingActiveTimeThreshold={summaryStats.meetingActiveTimeThreshold}
              meetingXPThreshold={summaryStats.meetingXPThreshold}
              isLoading={isLoading}
            />

            {/* Aggregate Time Table */}
            <AggregateTimeTable
              users={data?.users || []}
              isLoading={isLoading}
            />
          </>
        )}
      </div>
    </div>
  );
};
